import Color from 'color';

function sanitizeCssUrl(url) {
  if (!url || !/^https?:\/\//i.test(url)) return '';
  return url.replace(/['"<>()\\]/g, '');
}

export default function useDistributorBranding() {
  const branding = computed(() => useDistributorConfig().value?.identity ?? useWorkspaceStore().distributor?.identity);
  const hasCustomColor = computed(() => !!branding.value?.primaryColor);
  const hasCustomFont = computed(() => !!branding.value?.font?.regular);
  const hasDistributorBranding = computed(() => hasCustomColor.value || hasCustomFont.value);

  const cssVariables = computed(() => {
    const variables = [];

    // Add custom font-family when distributor has custom font
    if (hasCustomFont.value) {
      variables.push('font-family: DistributorFont, var(--font-sans)');
    }

    // Add color variables when distributor has custom primary color
    if (hasCustomColor.value) {
      try {
        const color = Color(branding.value.primaryColor);
        const primaryHex = color.hex();

        variables.push(
          `--color-primary-50: ${color.mix(Color('#fff'), 0.96).hex()}`,
          `--color-primary-100: ${color.mix(Color('#fff'), 0.88).hex()}`,
          `--color-primary-200: ${color.mix(Color('#fff'), 0.66).hex()}`,
          `--color-primary-300: ${color.mix(Color('#fff'), 0.44).hex()}`,
          `--color-primary-400: ${color.mix(Color('#fff'), 0.22).hex()}`,
          `--color-primary-500: ${primaryHex}`,
          `--color-primary-500/15: ${color.fade(0.85).string()}`,
          `--color-primary-500-15: ${color.fade(0.85).string()}`,
          `--color-primary-600: ${color.mix(Color('#000'), 0.22).hex()}`,
          `--color-primary-700: ${color.mix(Color('#000'), 0.44).hex()}`,
          `--color-primary-800: ${color.mix(Color('#000'), 0.66).hex()}`,
          `--color-primary-900: ${color.mix(Color('#000'), 0.88).hex()}`,
          `--el-color-primary: ${primaryHex}`,
        );
      } catch (error) {
        console.error('Invalid distributor primaryColor:', branding.value.primaryColor, error);
      }
    }

    return variables;
  });

  const customFontCss = computed(() => {
    if (!hasCustomFont.value) {
      return '';
    }

    const font = branding.value.font;
    const regular = sanitizeCssUrl(font.regular);
    const medium = sanitizeCssUrl(font.medium);
    const bold = sanitizeCssUrl(font.bold);

    return `
      @font-face {
        font-family: 'DistributorFont';
        font-style: normal;
        font-weight: 400;
        src: url('${regular}') format('woff');
      }
      @font-face {
        font-family: 'DistributorFont';
        font-style: normal;
        font-weight: 500;
        src: url('${medium}') format('woff');
      }
      @font-face {
        font-family: 'DistributorFont';
        font-style: normal;
        font-weight: 600;
        src: url('${bold}') format('woff');
      }
      @font-face {
        font-family: 'DistributorFont';
        font-style: normal;
        font-weight: 700;
        src: url('${bold}') format('woff');
      }`;
  });

  return {
    hasDistributorBranding,
    hasCustomColor,
    hasCustomFont,
    cssVariables,
    customFontCss,
  };
};
